import { toRaw } from "vue";
import Swal from "sweetalert2";
import { config } from "../config.js";
import { uniqBy } from "lodash";
import EventSource from "eventsource";
import dayjs from "dayjs";

let BASE_URL = "";
config.loadConfig().then((configResult) => {
  BASE_URL = configResult.api;
});

export default {
  namespaced: true,
  state: {
    isCanceled: false,
    chatHistory: {
      loading: false,
      pagination: {
        total: 0,
        page: 0,
        itemsPerPage: 0,
        lastPage: null,
        data: [],
      },
    },
    chatMessages: {
      loading: false,
      pagination: {
        nextCursor: null,
        limit: null,
        data: [],
      },
    },
    createChatLoading: false,
    selectedChat: null,
    messageGenerating: false,
    sendMessageLoading: false,
    currentChatRunId: null,
    cancelChatRunLoading: false,
  },
  mutations: {
    SET_IS_CANCELED: (state, data) => {
      state.isCanceled = data;
    },
    EMPTY_CURRENT_MESSAGE: (state) => {
      state.currentMessage = "";
    },
    SET_CHAT_HISTORY_LOADING: (state, data) => {
      state.chatHistory.loading = data;
    },
    SET_CHAT_HISTORY_PAGINATION: (state, data) => {
      state.chatHistory.pagination = data;
    },
    SET_CHAT_MESSAGES_LOADING: (state, data) => {
      state.chatMessages.loading = data;
    },
    SET_CHAT_MESSAGES_PAGINATION: (state, data) => {
      state.chatMessages.pagination = data;
    },
    SET_CREATE_CHAT_LOADING: (state, data) => {
      state.createChatLoading = data;
    },
    SET_SELECTED_CHAT: (state, data) => {
      state.selectedChat = data;
    },

    STORE_CHAT_MESSAGE: (state, data) => {
      const messageIndex = state.chatMessages.pagination.data.findIndex(
        (message) => message.id == data.id
      );
      if (messageIndex == -1) {
        state.chatMessages.pagination.data = [
          ...state.chatMessages.pagination.data,
          { ...data },
        ];
      }
    },

    UPDATE_CHAT_MESSAGE: (state, data) => {
      const messages = [...state.chatMessages.pagination.data];
      const messageIndex = messages.findIndex((msg) => msg.id == data.id);

      if (messageIndex === -1) {
        messages.push({
          ...data,
          content: data.content || "",
        });
      } else {
        const existingMessage = messages[messageIndex];
        const deltaContent = data.content || "";

        if (data.replace) {
          messages[messageIndex] = {
            ...existingMessage,
            content: deltaContent,
            attachments: data.attachments || existingMessage.attachments || [],
          };
        } else {
          messages[messageIndex] = {
            ...existingMessage,
            content: (existingMessage.content || "") + deltaContent,
            attachments: data.attachments || existingMessage.attachments || [],
          };
        }
      }

      state.chatMessages.pagination.data = messages;
    },

    REMOVE_SYSTEM_MESSAGES: (state) => {
      state.chatMessages.pagination.data =
        state.chatMessages.pagination.data.filter((m) => !m.system);
    },

    SET_MESSAGE_GENERATING: (state, data) => {
      state.messageGenerating = data;
    },
    SET_SEND_MESSAGE_LOADING: (state, data) => {
      state.sendMessageLoading = data;
    },
    SET_CHAT_TITLE: (state, { chatId, title }) => {
      const chatIndex = state.chatHistory.pagination.data.findIndex(
        (chat) => chat.id == chatId
      );
      if (chatIndex != -1) {
        state.chatHistory.pagination.data[chatIndex].title = title;
      }
      state.chatHistory.pagination.data = [
        ...state.chatHistory.pagination.data,
      ];
    },
    RESET_CHAT_MESSAGES: (state) => {
      state.chatMessages = {
        loading: false,
        pagination: {
          nextCursor: null,
          limit: null,
          data: [],
        },
      };
    },
    SET_RUN_ID: (state, data) => {
      state.currentChatRunId = data;
    },
    SET_CANCEL_RUN_LOADING: (state, data) => {
      state.cancelChatRunLoading = data;
    },
    MARK_LAST_USER_MESSAGE_FAILED: (state, errorPayload) => {
      const messages = [...state.chatMessages.pagination.data];
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === "user") {
          messages[i] = {
            ...messages[i],
            status: "failed",
            error_code: errorPayload?.error_code || "unknown",
            error_message:
              errorPayload?.message ||
              errorPayload?.error ||
              "Something went wrong while contacting the AI.",
            retryable: errorPayload?.retryable ?? false,
          };
          break;
        }
      }
      state.chatMessages.pagination.data = messages;
    },
    CLEAR_LAST_USER_MESSAGE_FAILURE: (state) => {
      const messages = [...state.chatMessages.pagination.data];
      for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].role === "user" && messages[i].status === "failed") {
          // eslint-disable-next-line no-unused-vars
          const { status, error_code, error_message, retryable, ...rest } =
            messages[i];
          messages[i] = { ...rest, status: "pending" };
          break;
        }
      }
      state.chatMessages.pagination.data = messages;
    },
  },
  actions: {
    getChats: function ({ commit, state }, payload) {
      const vm = this;
      commit("SET_CHAT_HISTORY_LOADING", true);
      let url = `bot-chat?`;
      if (payload) {
        if (payload.limit) {
          url = `${url}limit=${payload.limit}`;
        }
        if (payload.page) {
          url = `${url}&page=${payload.page}`;
        }
        if (payload.title) {
          url = `${url}&title=${payload.title}`;
        }
      }
      return new Promise((resolve) => {
        return vm.$http.get(url).then(
          (res) => {
            if (payload.limit) {
              if (payload.page == 1) {
                commit("SET_CHAT_HISTORY_PAGINATION", {
                  total: res.data.meta.total,
                  page: res.data.meta.current_page,
                  itemsPerPage: res.data.meta.per_page,
                  lastPage: res.data.meta.last_page,
                  data: res.data.data,
                });
              } else {
                commit("SET_CHAT_HISTORY_PAGINATION", {
                  total: res.data.meta.total,
                  page: res.data.meta.current_page,
                  itemsPerPage: res.data.meta.per_page,
                  lastPage: res.data.meta.last_page,
                  data: uniqBy(
                    [...state.chatHistory.pagination.data, ...res.data.data],
                    "id"
                  ),
                });
              }
            } else {
              commit("SET_CHAT_HISTORY_PAGINATION", {
                total: 0,
                page: 0,
                itemsPerPage: 0,
                lastPage: null,
                data: res.data.data,
              });
            }
            commit("SET_CHAT_HISTORY_LOADING", false);
            resolve(res);
          },
          (err) => {
            commit("SET_CHAT_HISTORY_LOADING", false);
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text: err?.data?.error?.message || "Something went wrong...",
                icon: "error",
              });
            }
            resolve(false);
          }
        );
      });
    },
    createChat: function ({ commit }) {
      const vm = this;
      commit("SET_CREATE_CHAT_LOADING", true);
      return new Promise((resolve) => {
        return vm.$http.post("bot-chat").then(
          (res) => {
            commit("SET_CREATE_CHAT_LOADING", false);
            resolve(res);
          },
          (err) => {
            commit("SET_CREATE_CHAT_LOADING", false);
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text: err?.data?.error?.message || "Something went wrong...",
                icon: "error",
              });
            }
            resolve(false);
          }
        );
      });
    },
    getChatMessages: function ({ commit, state }, payload) {
      commit("SET_CHAT_MESSAGES_LOADING", true);
      let url = `bot-chat/${payload.chatId}/messages?`;
      if (payload) {
        if (payload.limit) {
          url = `${url}limit=${payload.limit}`;
        }
        if (payload.cursor) {
          url = `${url}&cursor=${payload.cursor}`;
        }
      }
      let vm = this;
      return new Promise((resolve) => {
        return vm.$http.get(url).then(
          (res) => {
            const reversedArray = (res.data.data ?? []).reverse();
            if (payload.limit) {
              if (payload.cursor) {
                commit("SET_CHAT_MESSAGES_PAGINATION", {
                  nextCursor: res.data.meta.next_cursor,
                  limit: res.data.meta.per_page,
                  data: [
                    ...reversedArray,
                    ...state.chatMessages.pagination.data,
                  ],
                });
              } else {
                commit("SET_CHAT_MESSAGES_PAGINATION", {
                  nextCursor: res.data.meta.next_cursor,
                  limit: res.data.meta.per_page,
                  data: reversedArray,
                });
              }
            } else {
              commit("SET_CHAT_MESSAGES_PAGINATION", {
                nextCursor: null,
                limit: null,
                data: reversedArray,
              });
            }
            commit("SET_CHAT_MESSAGES_LOADING", false);
            resolve(res);
          },
          (err) => {
            commit("SET_CHAT_MESSAGES_LOADING", false);
            if (!err.accessDenied) {
              Swal.fire({
                title: "Error",
                text: err?.data?.error?.message || "Something went wrong...",
                icon: "error",
              });
            }
            resolve(false);
          }
        );
      });
    },
    sendChatMessage: function ({ commit }, payload) {
      commit("SET_SEND_MESSAGE_LOADING", true);
      const vm = this;
      return new Promise((resolve) => {
        return vm.$http
          .post(`bot-chat/${payload.chatId}/messages`, payload.data)
          .then(
            (res) => {
              commit("SET_SEND_MESSAGE_LOADING", false);
              resolve(res);
            },
            (err) => {
              commit("SET_SEND_MESSAGE_LOADING", false);
              if (!err.accessDenied) {
                Swal.fire({
                  title: "Error",
                  text: err?.data?.error?.message || "Something went wrong...",
                  icon: "error",
                });
              }
              resolve(false);
            }
          );
      });
    },
    uploadFile: function ({ commit }, payload) {
      commit("SET_SEND_MESSAGE_LOADING", true);
      const vm = this;
      let attachmentIndex = payload.message.attachments.findIndex(
        (attachment) => attachment.id == payload.attachmentId
      );
      if (attachmentIndex == -1) {
        return;
      }

      const rawFile = toRaw(payload.file);
      const blob = toRaw(rawFile.blob);
      const fileName = `${rawFile.name}.${rawFile.extension}`;
      const formData = new FormData();
      formData.append("file", blob, fileName);
      return new Promise((resolve) => {
        return vm.$http
          .post(`bot-chat/file`, formData, {
            onUploadProgress: (progress) => {
              const percentage = Math.round(
                (progress.loaded / progress.total) * 100
              );
              payload.message.attachments[attachmentIndex].progress =
                percentage;
            },
            headers: {
              "Content-Type": "multipart/form-data",
            },
          })
          .then(
            (res) => {
              delete payload.message.attachments[attachmentIndex].progress;
              payload.message.attachments[attachmentIndex].url =
                res.data.data.url;
              resolve(res.data.data);
            },
            (err) => {
              if (!err.accessDenied) {
                Swal.fire({
                  title: "Error",
                  text: err?.data?.error?.message || "Something went wrong...",
                  icon: "error",
                });
              }
              resolve(false);
            }
          );
      });
    },
    establishSSEConnection: function ({ commit, state }, chatId) {
      commit("SET_MESSAGE_GENERATING", true);
      const token = localStorage.getItem("ARSS_TOKEN");

      // Reconnect state — only used when the browser-level EventSource
      // drops the connection BEFORE we received any streamed assistant
      // content. Once content has started arriving we cannot safely
      // reconnect because OpenAI would generate a fresh response and
      // the user would see duplicated output.
      const MAX_RECONNECTS = 2;
      const RECONNECT_DELAYS_MS = [1000, 3000];
      let reconnectAttempt = 0;
      let receivedAnyContent = false;
      let receivedTerminalEvent = false;
      let manualClose = false;
      let evtSource = null;

      return new Promise((resolve) => {
        const open = () => {
          evtSource = new EventSource(
            `${BASE_URL}bot-chat/${chatId}/sse-messages`,
            {
              headers: {
                Authorization: "Bearer " + token,
              },
            }
          );
          attachListeners();
        };

        const safeClose = () => {
          manualClose = true;
          if (evtSource) {
            evtSource.close();
          }
        };

        const attachListeners = () => {
          evtSource.addEventListener(
            "thread.message.created",
            function (event) {
              const data = JSON.parse(event.data) || {};
              const message = data.message || null;
              if (message && message.role == "assistant") {
                receivedAnyContent = true;
                commit("REMOVE_SYSTEM_MESSAGES");
                commit("STORE_CHAT_MESSAGE", message);
              }
            }
          );

          evtSource.addEventListener("thread.message.delta", function (event) {
            const data = JSON.parse(event.data) || {};
            const message = data.message || null;
            if (state.isCanceled) {
              safeClose();
              commit("SET_MESSAGE_GENERATING", false);
              return;
            }
            if (message && message.role == "assistant") {
              receivedAnyContent = true;
              commit("UPDATE_CHAT_MESSAGE", message);
            }
          });

          evtSource.addEventListener(
            "thread.message.completed",
            function (event) {
              const data = JSON.parse(event.data) || {};
              const message = data.message || null;
              if (message) {
                message.replace = true;
                if (message.role == "assistant") {
                  receivedAnyContent = true;
                  commit("UPDATE_CHAT_MESSAGE", message);
                }
              }
            }
          );

          evtSource.addEventListener("thread.run.created", function (event) {
            const data = JSON.parse(event.data) || {};
            const runId = data.run_id || null;
            if (runId) {
              commit("SET_RUN_ID", runId);
            }

            if (data.thread_id || data.conversation_id) {
              let message = {
                id: `system_run_created_${runId || "unknown"}`,
                ...(data.thread_id ? { thread_id: data.thread_id } : {}),
                ...(data.conversation_id
                  ? { conversation_id: data.conversation_id }
                  : {}),
                content: "💭 Thinking...",
                role: "assistant",
                system: true,
                attachments: [],
                created_at: dayjs(),
              };
              commit("STORE_CHAT_MESSAGE", message);
            }
          });

          evtSource.addEventListener(
            "thread.run.in_progress",
            function (event) {
              const data = JSON.parse(event.data) || {};
              const runId = data.run_id || state.currentChatRunId || null;
              let message = {
                id: `system_run_created_${runId || "unknown"}`,
                ...(data.thread_id ? { thread_id: data.thread_id } : {}),
                ...(data.conversation_id
                  ? { conversation_id: data.conversation_id }
                  : {}),
                content: "💭 Thinking...",
                role: "assistant",
                system: true,
                replace: true,
                attachments: [],
                created_at: dayjs(),
              };
              commit("UPDATE_CHAT_MESSAGE", message);
            }
          );

          // Legacy: Assistants API step created (for tool_calls)
          evtSource.addEventListener(
            "thread.run.step.created",
            function (event) {
              const data = JSON.parse(event.data) || {};
              const step = data.step || null;
              if (step) {
                const runId = step.runId || null;
                const stepType = step.type || null;
                const threadId = step.threadId || null;
                if (stepType == "tool_calls") {
                  let message = {
                    id: runId,
                    thread_id: threadId,
                    content: "⏳ Analyzing...",
                    role: "assistant",
                    system: true,
                    attachments: [],
                    created_at: dayjs(),
                  };
                  commit("STORE_CHAT_MESSAGE", message);
                }
              }
            }
          );

          evtSource.addEventListener(
            "thread.run.requires_action",
            function (event) {
              const data = JSON.parse(event.data) || {};

              const toolType = data.tool_type || null;
              if (toolType) {
                const conversationId = data.conversation_id || null;
                commit("REMOVE_SYSTEM_MESSAGES");
                const systemMsgId = `system_${toolType}_${conversationId}`;

                let content = "💭 Thinking...";
                if (toolType === "web_search") {
                  content = "🔍 Searching the web...";
                } else if (toolType === "image_generation") {
                  content = "🖼️ Generating image...";
                } else if (toolType === "code_interpreter") {
                  content = "📊 Analyzing data...";
                }

                const existingMsg = state.chatMessages.pagination.data.find(
                  (m) => m.system && m.id === systemMsgId
                );

                if (existingMsg) return;

                let message = {
                  id: systemMsgId,
                  conversation_id: conversationId,
                  content: content,
                  role: "assistant",
                  system: true,
                  attachments: [],
                  created_at: dayjs(),
                };

                commit("STORE_CHAT_MESSAGE", message);
                return;
              }

              // Legacy Assistants API format (has tool_calls array)
              const threadId = data.thread_id || null;
              const runId = data.run_id || null;
              const toolCalls = data.tool_calls || [];
              if (threadId && runId && toolCalls.length > 0) {
                const toolCall = toolCalls[0] || null;
                if (!toolCall || !toolCall?.function?.name) {
                  console.warn(
                    "No tool call function name found, skipping message creation."
                  );
                  return;
                }
                let message = {
                  id: runId,
                  thread_id: threadId,
                  content: "",
                  role: "assistant",
                  system: true,
                  replace: true,
                  attachments: [],
                  created_at: dayjs(),
                };
                if (toolCall?.function?.name == "search_web") {
                  message.content = "🔍 Searching the web...";
                }
                if (toolCall?.function?.name == "generate_image") {
                  message.content = "🖼️ Generating an image...";
                }
                commit("UPDATE_CHAT_MESSAGE", message);
              }
            }
          );

          evtSource.addEventListener(
            "thread.run.step.completed",
            function (event) {
              const data = JSON.parse(event.data) || {};

              // Responses API format (has tool_type)
              const toolType = data.tool_type || null;
              if (toolType) {
                return;
              }

              // Legacy Assistants API format (has step object)
              const step = data.step || null;
              if (step) {
                const runId = step.runId || null;
                const stepType = step.type || null;
                const threadId = step.threadId || null;
                if (stepType == "tool_calls") {
                  let message = {
                    id: runId,
                    thread_id: threadId,
                    content: "✅ Analysis completed",
                    role: "assistant",
                    system: true,
                    replace: true,
                    attachments: [],
                    created_at: dayjs(),
                  };
                  commit("UPDATE_CHAT_MESSAGE", message);
                }
              }
            }
          );

          evtSource.addEventListener(
            "thread.run.step.failed",
            function (event) {
              const data = JSON.parse(event.data) || {};
              const step = data.step || null;
              if (step) {
                const stepId = step.id || null;
                const stepType = step.type || null;
                const threadId = step.threadId || null;
                if (stepType == "tool_calls") {
                  let message = {
                    id: stepId,
                    thread_id: threadId,
                    content: "❌ Error occurred while analyzing",
                    role: "assistant",
                    system: true,
                    replace: true,
                    attachments: [],
                    created_at: dayjs(),
                  };
                  commit("UPDATE_CHAT_MESSAGE", message);
                }
              }
            }
          );

          evtSource.addEventListener("thread.run.retrying", function (event) {
            const data = JSON.parse(event.data) || {};
            const message = {
              id: `system_retrying_${state.currentChatRunId || "current"}`,
              content:
                "🔄 " +
                (data.message || "Reconnecting to the AI…") +
                (data.attempt ? ` (attempt ${data.attempt})` : ""),
              role: "assistant",
              system: true,
              replace: true,
              attachments: [],
              created_at: dayjs(),
            };
            commit("UPDATE_CHAT_MESSAGE", message);
          });

          evtSource.addEventListener("thread.run.failed", function (event) {
            const data = JSON.parse(event.data) || {};
            const status = data.status || null;

            // Backward compat: older payloads only had { error: "string" }.
            // New payloads include error_code, message, retryable.
            const errorPayload = {
              error_code: data.error_code || "unknown",
              message:
                data.message ||
                data.error ||
                "Something went wrong while contacting the AI.",
              retryable: data.retryable ?? false,
            };

            receivedTerminalEvent = true;
            commit("REMOVE_SYSTEM_MESSAGES");
            commit("MARK_LAST_USER_MESSAGE_FAILED", errorPayload);
            commit("STORE_CHAT_MESSAGE", {
              id: `system_error_${Date.now()}`,
              content: `❌ ${errorPayload.message}`,
              role: "assistant",
              system: true,
              attachments: [],
              created_at: dayjs(),
            });

            if (
              !status ||
              [
                "failed",
                "cancelled",
                "cancelling",
                "expired",
                "incomplete",
              ].includes(status)
            ) {
              safeClose();
              commit("SET_MESSAGE_GENERATING", false);
              commit("SET_RUN_ID", null);
              resolve(true);
            }
          });

          evtSource.addEventListener("thread.run.completed", function () {
            receivedTerminalEvent = true;
            commit("REMOVE_SYSTEM_MESSAGES");
            safeClose();
            commit("SET_MESSAGE_GENERATING", false);
            commit("SET_RUN_ID", null);
            resolve(true);
          });

          evtSource.onerror = function (event) {
            // Ignore noise from intentional close after a terminal event.
            if (manualClose || receivedTerminalEvent) {
              return;
            }

            // If the user cancelled, treat as a clean stop.
            if (state.isCanceled) {
              safeClose();
              commit("SET_MESSAGE_GENERATING", false);
              commit("REMOVE_SYSTEM_MESSAGES");
              resolve(false);
              return;
            }

            // If we haven't received any content yet, the SSE handshake or the
            // very first events failed — this is the safe window to reconnect.
            if (!receivedAnyContent && reconnectAttempt < MAX_RECONNECTS) {
              const delay = RECONNECT_DELAYS_MS[reconnectAttempt] || 3000;
              reconnectAttempt += 1;

              // Tell the user we're reconnecting via the existing system bubble.
              commit("UPDATE_CHAT_MESSAGE", {
                id: `system_retrying_browser_${reconnectAttempt}`,
                content: `🔄 Reconnecting to the AI… (attempt ${reconnectAttempt})`,
                role: "assistant",
                system: true,
                replace: true,
                attachments: [],
                created_at: dayjs(),
              });

              try {
                evtSource.close();
              } catch (_) {
                // ignore
              }
              setTimeout(open, delay);
              return;
            }

            // Out of safe-reconnect window or attempts: surface the failure.
            safeClose();
            commit("SET_MESSAGE_GENERATING", false);
            commit("REMOVE_SYSTEM_MESSAGES");

            if (!receivedAnyContent) {
              const message =
                event?.message ||
                "We lost the connection to the AI. Please try again.";
              commit("MARK_LAST_USER_MESSAGE_FAILED", {
                error_code: "connection_lost",
                message,
                retryable: true,
              });
              commit("STORE_CHAT_MESSAGE", {
                id: `system_error_${Date.now()}`,
                content: `❌ ${message}`,
                role: "assistant",
                system: true,
                attachments: [],
                created_at: dayjs(),
              });
            }

            resolve(false);
          };
        };

        open();
      });
    },

    retryFailedMessage: function ({ commit, dispatch }, chatId) {
      commit("CLEAR_LAST_USER_MESSAGE_FAILURE");
      return dispatch("establishSSEConnection", chatId);
    },

    generateChatTitle: function ({ commit }, payload) {
      const vm = this;
      const { chatId, message } = payload;
      return new Promise((resolve) => {
        return vm.$http
          .post(`bot-chat/${chatId}/generate-title`, { message })
          .then(
            (res) => {
              commit("SET_CHAT_TITLE", {
                chatId: chatId,
                title: res.data.data.title,
              });
              resolve(res);
            },
            () => {
              resolve(false);
            }
          );
      });
    },
    cancelChatRun: function ({ commit }, payload) {
      commit("SET_CANCEL_RUN_LOADING", true);
      commit("SET_IS_CANCELED", true);
      const vm = this;
      return new Promise((resolve) => {
        return vm.$http
          .post(`bot-chat/${payload.chatId}/cancel`, payload.data)
          .then(
            (res) => {
              commit("SET_CANCEL_RUN_LOADING", false);
              commit("SET_RUN_ID", null);
              commit("SET_IS_CANCELED", false);
              resolve(res);
            },
            () => {
              commit("SET_CANCEL_RUN_LOADING", false);
              resolve(false);
            }
          );
      });
    },
    setSelectedChat: function ({ commit }, payload) {
      commit("SET_SELECTED_CHAT", payload);
    },
    storeChatMessage: function ({ commit, state }, payload) {
      commit("STORE_CHAT_MESSAGE", payload);
      return state.chatMessages.pagination.data.at(-1);
    },
    resetChatMessages: function ({ commit }) {
      commit("RESET_CHAT_MESSAGES");
    },
    stopGenerating: function ({ commit }) {
      commit("SET_IS_CANCELED", true);
    },
  },
};
